import { ALLOWED_ACCESS, CompanyModel } from '@/types/company'
import { UserModel } from '@/types/user'
import AccessService, { Access } from './AccessService'

/**
 *
 * Return True For thinsgs that are benefits
 * and false for things that are negative and take away
 * from user experience
 *
 */

type Cache = {
  [key: string]: boolean
}

export class UserAccessService implements AccessService {
  private user: UserModel
  private company: CompanyModel
  private cache: Cache = {}
  constructor(user: UserModel, company: CompanyModel) {
    this.user = user
    this.company = company
  }

  private updateCache(access: Access, hasAccess: boolean) {
    this.cache = { ...this.cache, [access]: hasAccess }
  }

  async canAccess(access: Access): Promise<boolean> {
    const saved = this.cache[access]
    if (saved !== undefined) {
      return saved
    }

    try {
      switch (access) {
        case Access.commandCenterDashboard:
          return this.hasAccessToCommandCenterDashboard()
        case Access.lineItemFilter:
          return this.hasAccessToLineItemFilter()
        case Access.broadcast:
          return this.hasAcessToBroadcast()
        case Access.bulkOptimizerSettingUpdate:
          return this.hasAccessToBulkOptimizerUpdate()
        case Access.companyDashboard:
          return this.hasAccessToCompanyDashboard()
        case Access.powerPointDownload:
          return this.hasAccessToPPT()
        case Access.searchWithoutCompanySelected:
          return this.hasAccessToSearchWithoutCompany()
        case Access.tritonAudioTopStation:
          return this.hasAccessToTritonAudioTopStations()
        case Access.bundleDashboardReportTray:
          return this.hasAccessToBundleDashboardReportTray()
        case Access.campaignDashboardReportTray:
          return this.hasAccessToCampaignDashboardReportTray()
        case Access.companyDashboardReportTray:
          return this.hasAccessToCompanyDashboardReportTray()
        case Access.viewScheduledReports:
          return this.hasAccessToViewScheduledReports()
        case Access.createScheduledReports:
          return this.hasAccessToCreateScheduledReports()
        case Access.jelliTtwnPremiere:
          return this.hasAccessToJelliTtwnPremiere()
        case Access.companyJelliTtwnPremiere:
          return this.hasAccessToCompanyJelliTtwnPremiere()
        case Access.canGetMaxAndMinImpressions:
          return this.hasAccessToSearchWithoutCompany()
        case Access.broadcastJelliLdFlagEnableInIhmDash:
          return this.hasBroadcastJelliLdFlagEnableInIhmDash()
        case Access.broadcastPremiereLdFlagEnableInIhmDash:
          return this.hasBroadcastPremiereLdFlagEnableInIhm_dash()
        case Access.broadcastTtwnLdFlagEnableInIhmDash:
          return this.hasBroadcastTtwnLdFlagEnableInIhmDash()
        case Access.salesDashboard:
          return this.hasAccessToSalesDashboard()
        case Access.dealDashboard:
          return this.hasAccessToDealDashboard()
        case Access.amazonReachAndFrequencyLdFlagEnableInIhmDash:
          return this.hasAccessToAmazonReachAndFrequency()
        case Access.magellanReachFrequencyInIhmDash:
          return this.hasMagellanReachFrequencyInIhmDash()
        case Access.broadcastGrpsInDash:
          return this.hasBroadcastGrpsInDash()
        case Access.dcmInIhmDash:
          return this.hasDcmInIhmDash()
        case Access.powerPointV2Download:
          return this.hasAccessToPPTXV2()
        case Access.hasPlaceIQEnableInIhmDash:
          return this.hasPlaceIqEnable()
        case Access.groundtruthBroadcastFootprint:
          return this.hasGroundtruthBroadcastFootprintEnable()
        case Access.groundtruthDigitalFootprint:
          return this.hasGroundtruthDigitalFootprintEnable()
        case Access.googleAnalyticsCampaignDashLink:
          return this.hasGA4Enable()
        case Access.searchTrendInCompanyDash:
          return this.hasSearchTrendInCompanyDash()
        case Access.hasSaleAgentChatInDash:
          return this.hasSalesAgentChatInSaleDash()
        case Access.nationalPPTRecapUpdates:
          return this.hasNationalPPTRecapUpdates()
        case Access.pptRecapLocalUpdate:
          return this.hasPptRecapLocalUpdate()
        case Access.beeswaxTotalAudioInDash:
          return this.hasAccessToBeeswaxTotalAudioInDash()
        case Access.groundtruthDoohInDash:
          return this.hasAccessToGroundtruthDoohInDash()
        case Access.vistarDoohInCampaignDash:
          return this.hasAccessToVistarDoohInCampaignDash()
        case Access.spotifyInCampaignDash:
          return this.hasAccessToSpotifyInCampaignDash()
        case Access.ianAmazonInCampaignDash:
          return this.hasAccessToIanAmazonInCampaignDash()
        case Access.iheartPodcastAmazonShopperSignalInDash:
          return this.hasAccessToIheartPodcastAmazonShopperSignalInDash()
        case Access.pptRecapCompanyBundle:
          return this.hasPptRecapCompanyBundle()
        case Access.downloadAllCsvInIhmDash:
          return this.hasAccessToDownloadAllCsv()
        case Access.platformSearchInDash:
          return this.hasAccessToPlatformSearchInDash()
        case Access.spotlightDashboardInDash:
          return this.hasAccessToSpotlightDashboardInDash()
        case Access.digitalAdSpendInIhmDash:
          return this.hasDigitalAdSpendInIhmDash()
        default:
          return false
      }
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCommandCenterDashboard(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('cmcc-in-ihm-dash')
      this.updateCache(Access.commandCenterDashboard, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }
  
  private async hasAccessToLineItemFilter(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('native-dashboard-lineitem-filtering')
      this.updateCache(Access.lineItemFilter, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAcessToBroadcast(): Promise<boolean> {
    try {
      const hasAccess = true
      this.updateCache(Access.broadcast, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCompanyDashboard(): Promise<boolean> {
    try {
      const isCompanyDashboardUser = this.company.isCompanyDashboardUser()
      const isUnifiedManager = this.company.isUnifiedManagedService()
      const hasAccess = isCompanyDashboardUser || isUnifiedManager
      this.updateCache(Access.companyDashboard, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToSearchWithoutCompany(): Promise<boolean> {
    try {
      const hasAccess = !this.company.hasAccess(ALLOWED_ACCESS.IHEART_REVISED_CAMPAIGN_SELECTION)
      this.updateCache(Access.searchWithoutCompanySelected, hasAccess)

      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToPPT(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToPPT()
      this.updateCache(Access.powerPointDownload, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToTritonAudioTopStations(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('triton-audio-top-stations')
      this.updateCache(Access.tritonAudioTopStation, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToBundleDashboardReportTray(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToReportTray()
      this.updateCache(Access.bundleDashboardReportTray, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCampaignDashboardReportTray(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToReportTray()
      this.updateCache(Access.campaignDashboardReportTray, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCompanyDashboardReportTray(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToReportTray()
      this.updateCache(Access.companyDashboardReportTray, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToViewScheduledReports(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToIhmReportTab()
      this.updateCache(Access.viewScheduledReports, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCreateScheduledReports(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToIhmReportTab()
      this.updateCache(Access.createScheduledReports, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToJelliTtwnPremiere(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('jelli-ttwn-premiere')
      this.updateCache(Access.jelliTtwnPremiere, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToCompanyJelliTtwnPremiere(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('company-jelli-ttwn-premiere')
      this.updateCache(Access.companyJelliTtwnPremiere, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasBroadcastJelliLdFlagEnableInIhmDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('jelli-in-initiative-group-dashboard')
      this.updateCache(Access.broadcastJelliLdFlagEnableInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasBroadcastPremiereLdFlagEnableInIhm_dash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('premiere-in-initiative-group-dashboard')
      this.updateCache(Access.broadcastPremiereLdFlagEnableInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasBroadcastTtwnLdFlagEnableInIhmDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('ttwn-in-initiative-group-dashboard')
      this.updateCache(Access.broadcastTtwnLdFlagEnableInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToSalesDashboard(): Promise<boolean> {
    try {
      const hasAccess = this.company.hasAccessToSalesDashboard()
      this.updateCache(Access.salesDashboard, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToDealDashboard(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('ui-deal-dashboard')
      this.updateCache(Access.dealDashboard, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToBulkOptimizerUpdate(): Promise<boolean> {
    try {
      const isAdmin = this.user.loginUser().isAdmin
      const ldEnabled = await this.user.isEnabled('bulk-optimizer-update-ui')
      const hasAccess = isAdmin && ldEnabled      
      this.updateCache(Access.bulkOptimizerSettingUpdate, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }
  
  private async hasAccessToAmazonReachAndFrequency(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('amazon-reach-and-frequency-in-dash')
      this.updateCache(Access.amazonReachAndFrequencyLdFlagEnableInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasMagellanReachFrequencyInIhmDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.magellanReachFrequencyInIhmDash)
      this.updateCache(Access.magellanReachFrequencyInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasBroadcastGrpsInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('broadcast-grps-in-dash')
      this.updateCache(Access.broadcastGrpsInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasDcmInIhmDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled('dcm-in-ihm-dash')
      this.updateCache(Access.dcmInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToPPTXV2(): Promise<boolean> {
    try {
      const [userAccess, companyAccess] = await Promise.all([
        this.user.isEnabled('powerpoint-template-poc'),
        this.company.hasAccessToPPT(),
      ])
      const hasAccess = userAccess && companyAccess
      this.updateCache(Access.powerPointDownload, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasPlaceIqEnable(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.hasPlaceIQEnableInIhmDash)
      this.updateCache(Access.hasPlaceIQEnableInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasGroundtruthBroadcastFootprintEnable(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.groundtruthBroadcastFootprint)
      this.updateCache(Access.groundtruthBroadcastFootprint, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasGroundtruthDigitalFootprintEnable(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.groundtruthDigitalFootprint)
      this.updateCache(Access.groundtruthDigitalFootprint, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasGA4Enable(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.googleAnalyticsCampaignDashLink)
      this.updateCache(Access.googleAnalyticsCampaignDashLink, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasSearchTrendInCompanyDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.searchTrendInCompanyDash)
      this.updateCache(Access.searchTrendInCompanyDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasSalesAgentChatInSaleDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.hasSaleAgentChatInDash)
      this.updateCache(Access.hasSaleAgentChatInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasNationalPPTRecapUpdates(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.nationalPPTRecapUpdates)
      this.updateCache(Access.nationalPPTRecapUpdates, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasPptRecapLocalUpdate(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.pptRecapLocalUpdate)
      this.updateCache(Access.pptRecapLocalUpdate, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToBeeswaxTotalAudioInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.beeswaxTotalAudioInDash)
      this.updateCache(Access.beeswaxTotalAudioInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToGroundtruthDoohInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.groundtruthDoohInDash)
      this.updateCache(Access.groundtruthDoohInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToVistarDoohInCampaignDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.vistarDoohInCampaignDash)
      this.updateCache(Access.vistarDoohInCampaignDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToSpotifyInCampaignDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.spotifyInCampaignDash)
      this.updateCache(Access.spotifyInCampaignDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToIanAmazonInCampaignDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.ianAmazonInCampaignDash)
      this.updateCache(Access.ianAmazonInCampaignDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToIheartPodcastAmazonShopperSignalInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.iheartPodcastAmazonShopperSignalInDash)
      this.updateCache(Access.iheartPodcastAmazonShopperSignalInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasPptRecapCompanyBundle(): Promise<boolean> {
    try {
      const [userAccess, companyAccess] = await Promise.all([
        this.user.isEnabled(Access.pptRecapCompanyBundle),
        Promise.resolve(this.company.hasAccessToPPT()),
      ])
      const hasAccess = userAccess && companyAccess
      this.updateCache(Access.pptRecapCompanyBundle, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToDownloadAllCsv(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.downloadAllCsvInIhmDash)
      this.updateCache(Access.downloadAllCsvInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToPlatformSearchInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.platformSearchInDash)
      this.updateCache(Access.platformSearchInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasAccessToSpotlightDashboardInDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.spotlightDashboardInDash)
      this.updateCache(Access.spotlightDashboardInDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }

  private async hasDigitalAdSpendInIhmDash(): Promise<boolean> {
    try {
      const hasAccess = await this.user.isEnabled(Access.digitalAdSpendInIhmDash)
      this.updateCache(Access.digitalAdSpendInIhmDash, hasAccess)
      return hasAccess
    } catch (e) {
      return false
    }
  }
}
